import { MP3_BITRATE, ensureMp3Encoder } from "./extractAudio";

/**
 * Joins several recordings of one lecture into a single MP3, back to back in
 * the order given, so they go through the pipeline - and come out the other
 * end - as one recording with one transcript and one summary.
 *
 * Done in the browser for the same reason a video's audio is: the server
 * takes one file per recording, and decoding here means only the joined
 * speech crosses the network. Every part is decoded, mixed down to mono and
 * brought to one sample rate, because an encoder takes a single format for
 * the whole stream while a phone and a laptop rarely agree on one.
 */

/** The rates an MP3 can carry; the output takes the first that covers every input. */
const MP3_RATES = [8_000, 11_025, 12_000, 16_000, 22_050, 24_000, 32_000, 44_100, 48_000];

/**
 * Streaming linear-interpolation resampler. Only ever upsamples in practice -
 * the output rate is the highest input rate - where linear is clean enough
 * for speech and needs no filter.
 */
class LinearResampler {
  private readonly step: number;
  /** Next output position, in input frames, where index 0 is `previous`. */
  private position = 1;
  private previous = 0;

  constructor(fromRate: number, toRate: number) {
    this.step = fromRate / toRate;
  }

  push(input: Float32Array): Float32Array {
    if (this.step === 1 || input.length === 0) return input;
    const length = input.length;
    const out = new Float32Array(Math.ceil((length - this.position) / this.step) + 1);
    let written = 0;
    while (this.position < length) {
      const index = Math.floor(this.position);
      const fraction = this.position - index;
      const a = index === 0 ? this.previous : input[index - 1];
      const b = input[index];
      out[written++] = a + (b - a) * fraction;
      this.position += this.step;
    }
    this.position -= length;
    this.previous = input[length - 1];
    return out.subarray(0, written);
  }
}

export interface JoinedAudio {
  file: File;
  durationMs: number;
}

export async function joinToMp3(
  files: File[],
  name: string,
  onProgress: (progress: number) => void,
  signal: AbortSignal,
): Promise<JoinedAudio> {
  const {
    ALL_FORMATS,
    AudioSample,
    AudioSampleSink,
    AudioSampleSource,
    BlobSource,
    BufferTarget,
    Input,
    Mp3OutputFormat,
    Output,
    Quality,
  } = await import("mediabunny");
  await ensureMp3Encoder();

  const inputs = files.map(
    (file) => new Input({ source: new BlobSource(file), formats: ALL_FORMATS }),
  );
  const target = new BufferTarget();
  const output = new Output({ format: new Mp3OutputFormat(), target });
  let started = false;
  try {
    // Every part is opened up front: the output rate depends on all of them,
    // and a file this browser cannot decode should fail before any encoding.
    const tracks = [];
    for (const [index, input] of inputs.entries()) {
      const track = await input.getPrimaryAudioTrack();
      if (track === null) throw new Error(`"${files[index].name}" has no audio track.`);
      if (!(await track.canDecode())) {
        throw new Error(`This browser cannot decode "${files[index].name}".`);
      }
      tracks.push(track);
    }
    const durations = await Promise.all(tracks.map((track) => track.computeDuration()));
    const total = durations.reduce((sum, seconds) => sum + Math.max(0, seconds), 0) || 1;
    const highest = Math.max(...tracks.map((track) => track.sampleRate));
    const sampleRate = MP3_RATES.find((rate) => rate >= highest) ?? 48_000;

    const source = new AudioSampleSource({
      codec: "mp3",
      quality: new Quality(MP3_BITRATE),
    });
    output.addAudioTrack(source);
    await output.start();
    started = true;

    let frames = 0;
    let done = 0;
    // Decoded samples are ~20 ms each; the bar only needs whole steps.
    let reported = -1;
    for (const [index, track] of tracks.entries()) {
      const resampler = new LinearResampler(track.sampleRate, sampleRate);
      let plane = new Float32Array(0);
      for await (const sample of new AudioSampleSink(track).samples()) {
        try {
          if (signal.aborted) throw new DOMException("Join cancelled.", "AbortError");
          // Encoder priming before zero is not part of the recording.
          const skip =
            sample.timestamp < 0
              ? Math.min(sample.numberOfFrames, Math.round(-sample.timestamp * sample.sampleRate))
              : 0;
          const count = sample.numberOfFrames - skip;
          if (count <= 0) continue;
          if (plane.length < count) plane = new Float32Array(count);
          const mono = new Float32Array(count);
          for (let channel = 0; channel < sample.numberOfChannels; channel++) {
            sample.copyTo(plane, {
              planeIndex: channel,
              format: "f32-planar",
              frameOffset: skip,
              frameCount: count,
            });
            for (let i = 0; i < count; i++) mono[i] += plane[i];
          }
          if (sample.numberOfChannels > 1) {
            for (let i = 0; i < count; i++) mono[i] /= sample.numberOfChannels;
          }
          const resampled = resampler.push(mono);
          if (resampled.length > 0) {
            const chunk = new AudioSample({
              data: resampled,
              format: "f32",
              numberOfChannels: 1,
              sampleRate,
              timestamp: frames / sampleRate,
            });
            await source.add(chunk);
            chunk.close();
            frames += resampled.length;
          }
          const progress = Math.min(1, (done + sample.timestamp + sample.duration) / total);
          if (progress - reported >= 0.005) {
            reported = progress;
            onProgress(progress);
          }
        } finally {
          sample.close();
        }
      }
      done += Math.max(0, durations[index]);
    }

    source.close();
    await output.finalize();
    if (target.buffer === null) throw new Error("Joining produced no audio.");
    return {
      file: new File([target.buffer], name, { type: "audio/mpeg", lastModified: Date.now() }),
      durationMs: Math.round((frames / sampleRate) * 1_000),
    };
  } catch (error) {
    if (started) await output.cancel().catch(() => undefined);
    throw error;
  } finally {
    for (const input of inputs) input.dispose();
  }
}
